/* eslint-disable react-portal/no-react-router-use-navigate */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, makeStyles, Typography } from '@material-ui/core';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans } from '@lingui/macro';
import { useNavigate } from 'react-router-dom';
import { i18n } from '@lingui/core';
import _ from 'lodash';

import { RowFlexLayout } from '../layout';
import NgpTextField from '../form/NgpTextField';
import NgpPasswordField from '../form/NgpPasswordField';
import { useRequestState } from '../../utilities/Hooks';
import { useGlobalDataService } from '../../services/ApiDataService';
import { rethrowError } from '../../utilities/RequestUtilities';
import Link from '../links/Link';
import { LoginBannerDialog, useWarningBanner } from './LoginBannerDialog';
import useSession from '../../stores/SessionStore';
import useEnvironment from '../../stores/EnvironmentStore';
import { useDashboardMenu } from '../../stores/DashboardMenuStore';
import { USE_FORM_DEFAULT_OPTIONS } from '../../utilities/FormUtilities';
import usePostLoginRedirect from './UsePostLoginRedirect';
import LoginWarnings from './LoginWarnings';
import AuthPanel from './AuthPanel';
import AuthButton from './AuthButton';
import AuthProgressButton from './AuthProgressButton';
import { LOGIN_ERRORS, LOGIN_ERROR_MESSAGES } from './LoginConstants';
import format, { FORMATTER_TYPES } from '../../utilities/FormattingUtilities';

export const LOGIN_URL = '/ngp/v2/login';
const CAC_LOGIN_URL = '/ngp/v2/login-cac';

const useLoginPageStyles = makeStyles((theme) => ({
    fieldContainer: {
        '& > *': {
            paddingBottom: theme.spacing(2),
        },
    },

    errorContainer: {
        marginTop: theme.spacing(0.5),
    },

    buttonContainer: {
        marginTop: theme.spacing(3),
    },

    warning: {
        color: theme.palette.warning.main,
    },
}));

export default function LoginPage() {
    const classes = useLoginPageStyles();
    const formMethods = useForm(USE_FORM_DEFAULT_OPTIONS);
    const [session] = useSession();
    const { loggedIn, login, isLoading, error, lastFailed, lastSuccess } = useLoginHandler();
    const navigate = useNavigate();
    const usernameRef = useRef();
    const { navigateToLastLocation } = usePostLoginRedirect();
    const { isLoading: loadingSamlConfig, config } = useLoginConfiguration();
    const [dashboardMenu] = useDashboardMenu();
    const isCac = config?.cacEnabled || config?.cacLdapEnabled;
    const isRedirectFromLogout = isCac && document.referrer.includes('logout');
    const { banner } = useWarningBanner();

    const emailOrUsername = formMethods.watch('emailOrUsername');

    useEffect(() => {
        if (config && isCac && !banner && !isRedirectFromLogout) {
            login({ source: config.cacEnabled ? 'cac_local' : 'cac_ldap' });
        }
    }, [config, login, isCac, banner, isRedirectFromLogout]);

    // this effect is triggered once we log in. Grab whether or not we need to change the
    // password, and navigate accordingly
    useEffect(() => {
        if (!session.initialized || !session.authorized || !loggedIn || !dashboardMenu.initialized) return;

        if (session.userInfo?.user?.changePassword) {
            navigate('/login/changePassword');
        } else {
            navigateToLastLocation();
        }
    }, [loggedIn, navigate, navigateToLastLocation, session, dashboardMenu]);

    return (
        <AuthPanel loading={!config || loadingSamlConfig}>
            <FormProvider {...formMethods}>
                {!isRedirectFromLogout && (
                    <LoginBannerDialog
                        onHide={() => {
                            if (!isCac) {
                                usernameRef.current.focus();
                            } else if (config) {
                                login({ source: config.cacEnabled ? 'cac_local' : 'cac_ldap' });
                            }
                        }}
                    />
                )}
                <form onSubmit={formMethods.handleSubmit(login)}>
                    <LoginWarnings />

                    {!isCac && (
                        <div className={classes.fieldContainer} id="sign-in-div">
                            <NgpTextField
                                fieldName="emailOrUsername"
                                label={<Trans>Email Address or Username</Trans>}
                                disabled={isLoading}
                                inputRef={usernameRef}
                                required
                                inputProps={{
                                    id: 'emailOrUsername',
                                    size: 65,
                                }}
                            />

                            <NgpPasswordField
                                fieldName="password"
                                label={<Trans>Password</Trans>}
                                disabled={isLoading}
                                required
                                inputProps={{
                                    id: 'password',
                                    size: 65,
                                }}
                            />
                        </div>
                    )}

                    {lastFailed && (
                        <div>Last attempted login failed: {format(lastFailed, { type: FORMATTER_TYPES.DATETIME })}</div>
                    )}

                    {lastSuccess && (
                        <div>Last successful login: {format(lastSuccess, { type: FORMATTER_TYPES.DATETIME })}</div>
                    )}

                    {isRedirectFromLogout && (
                        <>
                            <h3>User logout successful</h3>
                            <div>We recommend closing your browser to end the session.</div>
                        </>
                    )}

                    {!!error && (
                        <div className={classes.errorContainer}>
                            <Typography component="span" variant="subtitle2" color="error">
                                {i18n._(error)}
                            </Typography>
                        </div>
                    )}

                    {!isCac && (
                        <RowFlexLayout className={classes.buttonContainer}>
                            <AuthButton
                                component={Link}
                                variant="text"
                                to={`/reset/request?userNameOrEmail=${encodeURIComponent(emailOrUsername || '')}`}
                                disabled={isLoading}
                            >
                                <Trans>Forgot Password?</Trans>
                            </AuthButton>

                            <Box flexGrow={1} />

                            <AuthProgressButton type="submit" loading={isLoading} className="sign-in-button">
                                {isLoading ? <Trans>Signing In</Trans> : <Trans>Sign In</Trans>}
                            </AuthProgressButton>

                            {config?.samlEnabled && (
                                <Box ml={1}>
                                    <AuthButton color="secondary" component={Link} to="/ipm/sso/saml/ars">
                                        <Trans>Sign in with</Trans> {config.samlProviderName}
                                    </AuthButton>
                                </Box>
                            )}
                        </RowFlexLayout>
                    )}
                </form>
            </FormProvider>
        </AuthPanel>
    );
}

function useLoginHandler() {
    const dataService = useGlobalDataService();
    const [, sessionActions] = useSession();
    const [environment] = useEnvironment();
    const [, /* Not used */ dashboardMenuActions] = useDashboardMenu();
    const { data, isLoading, error, methods } = useRequestState();
    const [lastFailed, setLastFailed] = useState(false);
    const [lastSuccess, setLastSuccess] = useState(false);

    const login = useCallback(
        async (loginData) => {
            const { emailOrUsername, password, source } = loginData;

            try {
                methods.onInit();
                let authData;
                const isCac = source === 'cac_local' || source === 'cac_ldap';

                // try to login, we'll hit the catch if this fails
                if (!isCac) {
                    const postData = { emailOrUsername, password };
                    authData = await dataService.post(LOGIN_URL, postData, null, null, rethrowError);
                } else {
                    authData = await dataService.post(
                        CAC_LOGIN_URL,
                        { emailOrUsername: '', password: '' },
                        null,
                        null,
                        rethrowError
                    );
                }

                // try to refresh the session
                await sessionActions.refresh(environment);

                if (source !== 'cac_local') {
                    const showFailedLogin = checkLastFailed(authData?.user);
                    if (showFailedLogin) {
                        setLastFailed(showFailedLogin);
                        await timeout(1000);
                    } else if (authData?.user?.previousLoginSuccess) {
                        setLastSuccess(new Date(authData.user.previousLoginSuccess).getTime());
                        await timeout(1000);
                    }
                }

                // try to load the user's dashboard menu (needed to determine the user's default page)
                await dashboardMenuActions.load();

                methods.onLoad(true);
            } catch (e) {
                const errorType = e.responseBody?.userMessageKey;
                const message = _.get(
                    e,
                    'responseBody.userMessage',
                    _.get(e, 'responseBody.message', LOGIN_ERROR_MESSAGES[LOGIN_ERRORS.GENERIC])
                );

                methods.onError(LOGIN_ERROR_MESSAGES[errorType] || message);
            }
        },
        [dataService, methods, sessionActions, environment, dashboardMenuActions, setLastFailed]
    );

    return { isLoading, error, login, loggedIn: data === true, lastFailed, lastSuccess };
}

function checkLastFailed(user) {
    const lastFail = new Date(user.lastLoginFail).getTime() || 0;
    const lastSuccess = new Date(user.previousLoginSuccess).getTime() || 0;

    return lastFail > lastSuccess ? user.lastLoginFail : false;
}

function timeout(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

function useLoginConfiguration() {
    const dataService = useGlobalDataService();
    const { data: config, isLoading, methods } = useRequestState();

    useEffect(() => {
        getConfig();

        async function getConfig() {
            try {
                methods.onInit();
                methods.onLoad(await dataService.get('/ipm/public/ssoConfig/loginConfig'));
            } catch (e) {
                methods.onLoad({ samlEnabled: false });
            }
        }
    }, [dataService, methods]);

    return { isLoading, config };
}
